import { Fragment, type ReactNode, type RefObject } from 'react';
import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import { IconAlertTriangle, IconX } from '@tabler/icons-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';

import { cn } from '../../lib/cn';
import { DUR, EASE } from '../../interaction';
import { IconButton } from '../core/IconButton';
import { Spinner } from '../core/Spinner';

export type DialogSize = 'sm' | 'md' | 'lg';
export type DialogVariant = 'default' | 'confirm';
export type DialogTone = 'default' | 'danger';

const SIZES: Record<DialogSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-3xl',
};

/* Both transitions read `src/interaction.ts` at render time, never at module
   load: the durations and beziers are the token file's own values, so a change
   to `--dur-base` or `--ease-overshoot` reaches this file. Reading them at
   import time would freeze whatever the stylesheet happened to say before it
   applied. */
function backdropTransition(reduceMotion: boolean | null) {
  return reduceMotion ? { duration: 0 } : { duration: DUR.fast, ease: EASE.out };
}

/** The panel settles past its resting size — the motion form of the hard-shadow
 *  press on Button. */
function panelTransition(reduceMotion: boolean | null) {
  return reduceMotion ? { duration: 0 } : { duration: DUR.base, ease: EASE.overshoot };
}

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  /** Rendered through `Dialog.Description`, so it wires `aria-describedby`. */
  description?: ReactNode;
  /** The dialog body. Rendered inside the internal scroll container. */
  children?: ReactNode;
  /** Cancel / confirm row. `ConfirmDialog` supplies the destructive one. */
  footer?: ReactNode;
  size?: DialogSize;
  /**
   * `confirm` means "the user must answer": the popup becomes an
   * `alertdialog`. The framework itself is the same one either way — there is no
   * second modal implementation.
   */
  variant?: DialogVariant;
  tone?: DialogTone;
  /** Dims the body, blocks it, and announces a status while work is in flight. */
  loading?: boolean;
  loadingLabel?: string;
  /** Element focus lands on when the dialog opens. */
  initialFocus?: RefObject<HTMLElement | null>;
  /** Non-modal dialogs leave page scroll and outside pointer interaction alone. */
  modal?: boolean;
  /** Hides the corner close button (a confirm dialog that must be answered). */
  hideClose?: boolean;
  className?: string;
}

/**
 * Centered modal for focused work or a decision.
 *
 * The behaviour comes from Base UI's Dialog, which is the part that has to be
 * right: `modal` locks page scroll and disables outside pointer interaction
 * (`useScrollLock(open && modal === true, popupElement)` —
 * `@base-ui/react/dialog/root/useDialogRoot.js`), traps focus and moves it into
 * the popup on open (`FloatingFocusManager`, `dialog/popup/DialogPopup.js`),
 * restores focus to the element that was focused before the open — the trigger —
 * in that manager's unmount cleanup
 * (`floating-ui-react/components/FloatingFocusManager.js`), and closes on
 * Escape and on outside press (`useDismiss`, `disablePointerDismissal`).
 *
 * What this file adds is the composition, the house styling, the loading body,
 * and Motion — driven by `AnimatePresence` so the exit animation has an element
 * left to animate: the ports stay mounted (`keepMounted` on the Portal) and the
 * `hidden={false}` overrides stop Base UI from hiding a part that Base UI already
 * considers closed while we are still animating it out.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
  variant = 'default',
  tone,
  loading = false,
  loadingLabel,
  initialFocus,
  modal = true,
  hideClose = false,
  className,
}: DialogProps) {
  const reduceMotion = useReducedMotion();
  const isConfirm = variant === 'confirm';
  const resolvedTone = tone ?? (isConfirm ? 'danger' : 'default');
  const fade = backdropTransition(reduceMotion);

  return (
    <BaseDialog.Root open={open} onOpenChange={onOpenChange} modal={modal}>
      <BaseDialog.Portal keepMounted>
        <AnimatePresence>
          {open ? (
            <Fragment key="dialog">
              {/* No backdrop when the dialog is non-modal: the page behind it
                  stays live by definition, and darkening it would say
                  otherwise. */}
              {modal ? (
                <BaseDialog.Backdrop
                  hidden={false}
                  className="fixed inset-0 z-[var(--z-overlay)] bg-overlay"
                  render={
                    <motion.div
                      initial={reduceMotion ? false : { opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={fade}
                    />
                  }
                />
              ) : null}

              {/* Centering wrapper: a Motion transform on the panel would
                  clobber Tailwind's translate utilities, so the panel is
                  centered by flex instead of by transform.

                  `--z-modal` (500) sits above `--z-overlay` (400) and BELOW
                  `--z-popover` (600), `--z-tooltip` (700) and `--z-toast` (800):
                  a tooltip raised from inside a dialog and a toast announcing
                  the result of one both belong on top of it. Before the tokens
                  were consumed, all seven surfaces hardcoded `z-50` and a toast
                  cleared a modal only by being inserted into the DOM later. */}
              <div className="pointer-events-none fixed inset-0 z-[var(--z-modal)] flex items-center justify-center p-4">
                <BaseDialog.Popup
                  hidden={false}
                  data-focus-ring="none"
                  role={isConfirm ? 'alertdialog' : 'dialog'}
                  aria-modal={modal ? true : undefined}
                  aria-busy={loading || undefined}
                  initialFocus={initialFocus}
                  className={cn(
                    'pointer-events-auto flex max-h-[calc(100vh-2rem)] w-full flex-col',
                    'rounded-xs border-2 bg-surface shadow-hard',
                    resolvedTone === 'danger' ? 'border-danger' : 'border-line-strong',
                    SIZES[size],
                    className,
                  )}
                  render={
                    <motion.div
                      initial={reduceMotion ? false : { opacity: 0, scale: 0.96 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.98 }}
                      transition={panelTransition(reduceMotion)}
                    />
                  }
                >
                  <header className="flex shrink-0 items-start gap-4 border-b border-line-subtle px-6 py-4">
                    {resolvedTone === 'danger' ? (
                      <IconAlertTriangle className="mt-0.5 shrink-0 text-danger" size={20} aria-hidden="true" />
                    ) : null}
                    <div className="min-w-0 flex-1">
                      <BaseDialog.Title className="font-display text-lg font-semibold uppercase tracking-title text-heading">
                        {title}
                      </BaseDialog.Title>
                      {description ? (
                        <BaseDialog.Description className="mt-1 text-sm text-muted">
                          {description}
                        </BaseDialog.Description>
                      ) : null}
                    </div>
                    {hideClose ? null : (
                      <BaseDialog.Close
                        render={<IconButton label="Close dialog" icon={<IconX size={16} />} size="sm" />}
                      />
                    )}
                  </header>

                  <div className="relative min-h-0 flex-1 overflow-y-auto px-6 py-5">
                    <div className={cn('text-sm text-body', loading && 'pointer-events-none opacity-40')}>
                      {children}
                    </div>
                    {loading ? (
                      <div className="absolute inset-0 grid place-items-center bg-surface/70">
                        <Spinner size="lg" label={loadingLabel ?? 'Loading'} />
                      </div>
                    ) : null}
                  </div>

                  {footer ? (
                    <footer className="flex shrink-0 items-center justify-end gap-3 border-t border-line-subtle px-6 py-4">
                      {footer}
                    </footer>
                  ) : null}
                </BaseDialog.Popup>
              </div>
            </Fragment>
          ) : null}
        </AnimatePresence>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}
